import { useEffect, useRef, useCallback, useState } from 'react';

/**
 * Auto-refresh hook that polls a function at a given interval.
 * Returns { data, error, loading, refresh }.
 */
export function usePolling<T>(
  fetcher: () => Promise<T>,
  intervalMs: number = 30_000,
  enabled: boolean = true,
) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const refresh = useCallback(async () => {
    try {
      setError(null);
      const result = await fetcherRef.current();
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;

    // Initial fetch
    refresh();

    // Set up polling
    timerRef.current = setInterval(refresh, intervalMs);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [refresh, intervalMs, enabled]);

  return { data, error, loading, refresh };
}
